import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Chip, ContentCard, IconButton, LeafletMap, MediaCarousel, SectionHeader, formatRangeLabel } from "@zorni/components-lib";

import RecapForms from "../recapForms/RecapForms";
import TripDetails from "../tripDetails/TripDetails";
import { useToggleFavorite } from "../../trips/favorites/hooks/useToggleFavorite";
import { useZorni } from "../../../hooks/useZorni";
import { useRequireAuth } from "../../../hooks/useRequireAuth";
import styles from "./DetailsTrip.module.css";

const emptyAmount = { value: 0, currency: "" };

const normalizeProgramItem = (item, index, type) => {
  if (typeof item === "string") {
    return {
      id: `${type}-${index}-${item}`,
      title: item,
      picture: "",
      description: "",
      isIncluded: false,
      adultPricePerPerson: emptyAmount,
      childPricePerPerson: emptyAmount,
      babiesPricePerPerson: emptyAmount,
    };
  }

  const title = item?.title ?? item?.name ?? item?.label ?? "";
  const picture =
    item?.picture ?? item?.image ?? item?.photo?.link ?? item?.pictures?.[0]?.link ?? "";

  return {
    ...item,
    id: item?.id ?? `${type}-${index}-${title || "item"}`,
    title,
    picture,
    description: item?.description ?? "",
    isIncluded: !!item?.isIncluded,
    adultPricePerPerson: item?.adultPricePerPerson ?? emptyAmount,
    childPricePerPerson: item?.childPricePerPerson ?? emptyAmount,
    babiesPricePerPerson: item?.babiesPricePerPerson ?? emptyAmount,
  };
};

const normalizeProgramItems = (items, type) =>
  (Array.isArray(items) ? items : []).map((item, index) => normalizeProgramItem(item, index, type));

const asString = (value) => (value === null || value === undefined ? "" : String(value));

const distinct = (values) => {
  const seen = new Set();
  return values
    .map((v) => String(v || "").trim())
    .filter((v) => {
      const key = v.toLowerCase();
      if (!v || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const toDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const monthLabel = (date, locale) =>
  date ? date.toLocaleDateString(locale, { month: "short" }).replace(/\.$/, "") : "";

/**
 * Détail d'un voyage — « 03 - tripDetails ».
 *
 *   Fil d'Ariane · sur-titre · titre · destination · pastilles · favori
 *   ┌ galerie ─────────────────────┐ ┌ réservation (collante) ┐
 *   ├ description, itinéraire,     │ │                        │
 *   │ carte, avis, agence          │ └────────────────────────┘
 *
 * `children` : sections ajoutées en fin de colonne principale (avis, agence).
 */
const DetailsTrip = ({
  tripPublicationId,
  tripFlight,
  nbAdults,
  nbChilds,
  nbBabies,
  tripDateId,
  navigate,
  publicationId,
  establishmentId,
  children,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "fr-FR";
  const [selectedTripDateSummary, setSelectedTripDateSummary] = useState(null);

  // Options et activités choisies : restaurées au retour de /booking.
  const acquisitionsKey = tripPublicationId ? `acquisitions_${tripPublicationId}` : null;
  const [acquisitions, setAcquisitions] = useState(() => {
    if (!acquisitionsKey) return {};
    try {
      const saved = sessionStorage.getItem(acquisitionsKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!acquisitionsKey) return;
    try {
      sessionStorage.setItem(acquisitionsKey, JSON.stringify(acquisitions));
    } catch {
      /* quota dépassé ou navigation privée */
    }
  }, [acquisitions, acquisitionsKey]);

  const tripSummary = tripFlight?.tripSummary;

  const allTripDateSummaries = useMemo(
    () => tripFlight?.content?.flatMap((flight) => flight?.tripDateSummary ?? []) ?? [],
    [tripFlight]
  );

  const resolvedTripDateSummary = useMemo(() => {
    if (selectedTripDateSummary?.id) return selectedTripDateSummary;
    return (
      allTripDateSummaries.find((d) => asString(d?.id) === asString(tripDateId)) ??
      allTripDateSummaries[0] ??
      null
    );
  }, [allTripDateSummaries, selectedTripDateSummary, tripDateId]);

  const tripProgramDetails = useMemo(() => {
    const ordered = [];
    const seen = new Set();

    allTripDateSummaries.forEach((tripDateSummary, tripDateIndex) => {
      (tripDateSummary?.stepsDate ?? []).forEach((stepDate, stepDateIndex) => {
        (stepDate?.tripProgramDetails ?? []).forEach((step, stepIndex) => {
          const stepId =
            step?.id ?? `${tripDateSummary?.id ?? tripDateIndex}-${stepDateIndex}-${step?.ranking?.rank ?? stepIndex}`;
          if (seen.has(stepId)) return;

          seen.add(stepId);
          ordered.push({
            ...step,
            id: step?.id ?? stepId,
            activities: normalizeProgramItems(
              (Array.isArray(step?.activities) && step.activities) ||
                (Array.isArray(step?.activites) && step.activites) ||
                [],
              "activity"
            ),
            options: normalizeProgramItems(step?.options ?? [], "option"),
          });
        });
      });
    });

    return ordered.sort((a, b) => (a?.ranking?.rank ?? Infinity) - (b?.ranking?.rank ?? Infinity));
  }, [allTripDateSummaries]);

  /* ------------------------------------------------------------- En-tête */
  const countries = distinct(tripProgramDetails.map((s) => s?.country));
  const cities = distinct(tripProgramDetails.map((s) => s?.city));
  const destinationLine = [countries.join(" · "), cities.join(", ")].filter(Boolean).join(" — ");

  const nbDays = resolvedTripDateSummary?.nbDays;
  const nbNights = resolvedTripDateSummary?.nbNight;

  const departures = useMemo(
    () =>
      (tripFlight?.content ?? [])
        .filter((c) => !c?.isFlightIncluded && !c?.departureInfo)
        .flatMap((c) => c?.tripDateSummary ?? [])
        .map((d) => toDate(d?.startDate))
        .filter(Boolean)
        .sort((a, b) => a - b),
    [tripFlight]
  );

  const departuresLabel = (() => {
    if (!departures.length) return "";
    const first = monthLabel(departures[0], locale);
    const last = monthLabel(departures[departures.length - 1], locale);
    const months = first === last ? first : `${first} – ${last}`;
    return departures.length > 1
      ? t("tripDetails.header.departures", {
          count: departures.length,
          months,
          defaultValue: `${departures.length} départs · ${months}`,
        })
      : t("tripDetails.header.departure", { months, defaultValue: `1 départ · ${months}` });
  })();

  const stars = tripProgramDetails.map((s) => Number(s?.hosting?.stars)).filter((n) => n > 0);
  const starsLabel = stars.length
    ? Math.min(...stars) === Math.max(...stars)
      ? `${Math.min(...stars)}★`
      : `${Math.min(...stars)}–${Math.max(...stars)}★`
    : "";

  const pills = [
    nbDays && {
      icon: "iconClock",
      label: t("tripDetails.header.duration", {
        days: nbDays,
        nights: nbNights,
        defaultValue: nbNights ? `${nbDays} j / ${nbNights} n` : `${nbDays} j`,
      }),
    },
    departuresLabel && { icon: "iconCalendar", label: departuresLabel },
    starsLabel && {
      icon: "iconHotel",
      label: t("tripDetails.header.hosting", { stars: starsLabel, defaultValue: `Hébergements ${starsLabel}` }),
    },
  ].filter(Boolean);

  /* ------------------------------------------------------------- Favori */
  const { userId } = useZorni();
  const requireAuth = useRequireAuth();
  const { toggleFavorite } = useToggleFavorite();
  // QR_GET_TRIP_PUBLICATION_SUMMARY ne reçoit jamais l'utilisateur courant
  // côté controller (pas de personId dans ce resolver) : isFavorite/favoriteId
  // y sont donc toujours false/null, quel que soit l'utilisateur. Appeler
  // cette requête ici ne faisait donc que rejouer ces mêmes valeurs par
  // défaut après un aller-retour réseau inutile.
  const [favorite, setFavorite] = useState({ isFavorite: false, favoriteId: null });

  const handleFavorite = () =>
    requireAuth(async () => {
      const previous = favorite;
      setFavorite((f) => ({ ...f, isFavorite: !f.isFavorite }));
      const result = await toggleFavorite(userId, { id: tripPublicationId, ...previous });
      if (!result?.success) setFavorite(previous);
      else if (result.mode === "added") {
        setFavorite({ isFavorite: true, favoriteId: result.favorite?.id ?? previous.favoriteId });
      }
    });

  /* ------------------------------------------------------------ Galerie */
  const galleryImages = useMemo(() => {
    const seen = new Set();
    const tripPictures = [...(tripSummary?.pictures ?? [])].sort((a, b) => Number(b?.isMain) - Number(a?.isMain));
    return [
      ...tripPictures.map((p) => ({ src: p?.link, alt: tripSummary?.title })),
      ...tripProgramDetails.map((s) => ({ src: s?.picture?.link, alt: s?.city || s?.ranking?.title })),
    ].filter((img) => {
      if (!img.src || seen.has(img.src)) return false;
      seen.add(img.src);
      return true;
    });
  }, [tripSummary, tripProgramDetails]);

  const galleryBadge = resolvedTripDateSummary
    ? formatRangeLabel(toDate(resolvedTripDateSummary.startDate), toDate(resolvedTripDateSummary.endDate))
    : "";

  /* -------------------------------------------------------------- Carte */
  const mapMarkers = useMemo(
    () =>
      tripProgramDetails
        .filter((s) => Number.isFinite(Number(s?.latitude)) && Number.isFinite(Number(s?.longitude)))
        .filter((s) => !(Number(s.latitude) === 0 && Number(s.longitude) === 0))
        .map((s) => ({ lat: Number(s.latitude), lng: Number(s.longitude), name: s.city || s?.ranking?.title })),
    [tripProgramDetails]
  );

  // Référence stable : LeafletMap recrée la carte (et perd ses marqueurs)
  // si `center` change d'identité à chaque rendu.
  const mapCenter = useMemo(
    () => (mapMarkers.length ? [mapMarkers[0].lat, mapMarkers[0].lng] : null),
    [mapMarkers]
  );

  // Leaflet a besoin d'un conteneur mesuré : on attend sa taille réelle.
  const mapWrapperRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  useLayoutEffect(() => {
    const el = mapWrapperRef.current;
    if (!el) return undefined;
    const check = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setMapReady(true);
    };
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mapMarkers.length]);

  const description = tripSummary?.detailedDescription || tripSummary?.smallDescription;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            { label: t("tripDetails.header.results", "Résultats"), to: "/trips" },
            { label: tripSummary?.title },
          ]}
        />

        <header className={styles.intro}>
          <div className={styles.introText}>
            <SectionHeader
              size="page"
              className={styles.title}
              kicker={t("tripDetails.header.kicker", "Voyage organisé")}
              title={tripSummary?.title}
            />
            {destinationLine && <p className={styles.destination}>{destinationLine}</p>}
            {pills.length > 0 && (
              <div className={styles.pills}>
                {pills.map((p) => (
                  <Chip key={p.icon} variant="meta" iconName={p.icon} label={p.label} />
                ))}
              </div>
            )}
          </div>
          <IconButton
            variant="outline"
            className={favorite.isFavorite ? styles.favActive : undefined}
            iconName={favorite.isFavorite ? "iconFavoriteSolid" : "iconFavoriteRegular"}
            aria-label={
              favorite.isFavorite
                ? t("tripDetails.header.removeFavorite", "Retirer des favoris")
                : t("tripDetails.header.addFavorite", "Ajouter aux favoris")
            }
            onClick={handleFavorite}
          />
        </header>

        <div className={styles.layout}>
          {galleryImages.length > 0 && (
            <section className={styles.gallery} aria-label={t("tripDetails.gallery", "Galerie photos")}>
              <MediaCarousel t={t}
                variant="gallery"
                items={galleryImages}
                showThumbnails
                autoScroll={false}
                badge={galleryBadge}
                ariaLabel={t("tripDetails.gallery", "Galerie photos")}
              />
            </section>
          )}

          <aside className={styles.aside} aria-label={t("tripDetails.booking.aria", "Réservation")}>
            <RecapForms
              tripPublicationId={tripPublicationId}
              nbAdults={nbAdults}
              nbChilds={nbChilds}
              nbBabies={nbBabies}
              tripDateId={tripDateId}
              acquisitions={acquisitions}
              tripFlight={tripFlight}
              onTripDateChange={setSelectedTripDateSummary}
              navigate={navigate}
              publicationId={publicationId}
              establishmentId={establishmentId}
            />
          </aside>

          <div className={styles.main}>
            {description && (
              <ContentCard title={t("tripDetails.description", "Description")} titleId="trip-description">
                <p className={styles.description}>{description}</p>
              </ContentCard>
            )}

            <TripDetails
              tripProgramDetails={tripProgramDetails}
              acquisitions={acquisitions}
              setAcquisitions={setAcquisitions}
              selectedTripDateSummary={resolvedTripDateSummary}
            />

            {mapMarkers.length > 0 && (
              <ContentCard title={t("tripDetails.map", "Carte du voyage")} titleId="trip-map">
                <div ref={mapWrapperRef} className={styles.map}>
                  {mapReady && (
                    <LeafletMap
                      theme="light"
                      markers={mapMarkers}
                      center={mapCenter}
                      fitBounds
                      showAttribution
                      /* Le fond CARTO par défaut de la lib exige désormais une
                         clé : ses tuiles reviennent en filigrane « API KEY
                         REQUIRED ». On passe sur OpenStreetMap en attendant que
                         l'équipe arrête un fournisseur. */
                      tileUrl="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                      tileAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                  )}
                </div>
              </ContentCard>
            )}

            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsTrip;
